import { ProviderCard } from 'components/integrations';
import { useRef } from 'react';
import { useLocation } from 'react-router';
import { useGetStoreInfoQuery } from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';

export const AddIntegrations = () => {
  const containerRef = useRef<any>(null);
  const location = useLocation();

  const { rawQueryParams, serializedStoreName } = useParseURLQueryParams();

  const { data: storeInfo } = useGetStoreInfoQuery(
    String(serializedStoreName),
    {
      skip: !!!rawQueryParams?.store_name,
    }
  );

  const hasEspConnected = Boolean(
    storeInfo?.integrations.find(
      (esp) => esp.category.toLowerCase() === 'email'
    )
  );

  return (
    <section className="mt-20">
      <p className="font-bold leading-10 text-segments-44px">Connect new</p>
      <p className="mt-6 text-base">
        Discover new integrations to connect your existing marketing channels
        and leverage Segments data insights.
      </p>
      {/* Category Starts */}
      <div className="mt-10">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl leading-7">Popular Email Marketing Apps</h3>
            <p className="mt-2 text-black text-opacity-30">
              Select one provider. Connect an email marketing app to sync your
              segments for direct marketing.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-6 mt-4" ref={containerRef}>
          <ProviderCard
            description="Automatically sync your customer segments into Klaviyo and start building some marketing flows."
            provider="Klaviyo"
            disableConnect={hasEspConnected}
            linkTo={{
              pathname: '/integrations/connect/klaviyo',
              state: { background: location },
              search: location.search,
            }}
          />

          <ProviderCard
            description="Create audiences from our customer segments in Mailchimp for your marketing campaigns."
            provider="MailChimp"
            disableConnect={hasEspConnected}
            linkTo={{
              pathname: '/integrations/connect/mailchimp',
              state: { background: location },
              search: location.search,
            }}
          />

          <ProviderCard
            description="Save hours daily by automating your email marketing and spend more time on your business."
            provider="ActiveCampaign"
            disableConnect={hasEspConnected}
            linkTo={{
              pathname: '/integrations/connect/activecampaign',
              state: { background: location },
              search: location.search,
            }}
          />

          <ProviderCard
            description="Create and automate ecommerce campaigns with pre-built, customizable templates."
            provider="Drip"
            disableConnect={hasEspConnected}
            linkTo={{
              pathname: '/integrations/connect/drip',
              state: { background: location },
              search: location.search,
            }}
          />

          <ProviderCard
            description="Create and automate ecommerce campaigns with pre-built, customizable templates."
            provider="Omnisend"
            disableConnect={hasEspConnected}
            linkTo={{
              pathname: '/integrations/connect/omnisend',
              state: { background: location },
              search: location.search,
            }}
          />
        </div>
        <div className="mt-4">
          <p className="text-black text-segments-13px text-opacity-30">
            Showing 5 of 5 email service providers ready to integrate.
          </p>
        </div>
      </div>
      {/* Category Ends */}
      {/* Category Starts */}
      <div className="mt-16">
        <div className="flex items-center justify-between ">
          <div>
            <h3 className="text-2xl leading-7">Digital Ads</h3>
            <p className="mt-2 text-black text-opacity-30">
              Connect your advertising channels to enable direct syncing of
              targeted customer segments.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-6 mt-4">
          <ProviderCard
            description="Integrate with Facebook and sync your customer segments as Custom Audiences."
            provider="Facebook"
            linkTo={{
              pathname: '/integrations/connect/facebook',
              state: { background: location },
              search: location.search,
            }}
          />
          <ProviderCard
            description="Integrate with Google Ads and sync your customer segments as Customer Lists."
            provider="Google"
            linkTo={{
              pathname: '/integrations/connect/google',
              state: { background: location },
              search: location.search,
            }}
          />
        </div>
        <div className="mt-4">
          <p className="text-black text-segments-13px text-opacity-30">
            Showing 2 of 2 digital ads provider ready to integrate.
          </p>
        </div>
      </div>
      {/* Category Ends */}
      {/* Category Starts */}
      <div className="mt-16">
        <div className="flex items-center justify-between ">
          <div>
            <h3 className="text-2xl leading-7">SMS</h3>
            <p className="mt-2 text-black text-opacity-30">
              Connect an SMS integration to start sending SMS messages directly
              to your synced customer segments.
            </p>
          </div>
        </div>
        <div className="flex mt-4 space-x-6 overflow-x-auto">
          <ProviderCard
            description="SMS marketing for growing Shopify stores."
            provider="Postscript"
            linkTo={{
              pathname: '/integrations/connect/postscript',
              state: { background: location },
              search: location.search,
            }}
          />
          <ProviderCard
            description="Customer reviews, visual marketing, loyalty, referrals, and SMS marketing."
            provider="Yotpo"
            linkTo={{
              pathname: '/integrations/connect/yotpo',
              state: { background: location },
              search: location.search,
            }}
          />
        </div>
        <div className="mt-4">
          <p className="text-black text-segments-13px text-opacity-30">
            Showing 2 of 2 SMS providers ready to integrate.
          </p>
        </div>
      </div>
      {/* Category Ends */}
      {/* Category Starts */}
      <div className="mt-16">
        <div className="flex items-center justify-between ">
          <div>
            <h3 className="text-2xl leading-7">Reviews</h3>
            <p className="mt-2 text-black text-opacity-30">
              Connect your customer reviews and loyalty apps for enhanced
              segmentation capabilities.
            </p>
          </div>
        </div>
        <div className="flex mt-4 space-x-6 overflow-x-auto">
          <ProviderCard
            description="Customer reviews, visual marketing, loyalty, referrals, and SMS marketing."
            provider="Yotpo"
            linkTo={{
              pathname: '/integrations/connect/yotpo',
              state: { background: location },
              search: location.search,
            }}
            comingSoon
          />
        </div>
        <div className="mt-4">
          <p className="text-black text-segments-13px text-opacity-30">
            Showing 1 of 1 Review and loyalty providers ready to integrate.
          </p>
        </div>
      </div>
      {/* Category Ends */}
    </section>
  );
};
